import { DISPLAY_STATUS, normalizeDisplayStatus } from "./pricingOptions";

const toCents = (n) => Math.round((Number(n) || 0) * 100);
const fromCents = (c) => (c || 0) / 100;
const pickNumber = (...vals) => {
  for (const v of vals) {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
};
const pickBool = (...vals) => vals.some((v) => v === true || String(v).toLowerCase() === "true");
const pickMarginPct = (...vals) => {
  for (const v of vals) {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
};
const allocateByQuantityExact = (quantities = [], totalCents = 0) => {
  const safeTotal = Math.max(0, Number(totalCents) || 0);
  const q = quantities.map((n) => Math.max(0, Number(n) || 0));
  const totalUnits = q.reduce((sum, n) => sum + n, 0);
  if (!totalUnits || !safeTotal) return q.map(() => 0);

  const basePerUnit = Math.floor(safeTotal / totalUnits);
  let remainder = safeTotal - (basePerUnit * totalUnits);
  const allocated = q.map((qty) => qty * basePerUnit);

  for (let i = 0; i < q.length && remainder > 0; i += 1) {
    const extra = Math.min(remainder, q[i]);
    allocated[i] += extra;
    remainder -= extra;
  }
  return allocated;
};

const truthy = (v) => v === true || String(v).toLowerCase() === "true";

export const getEffectiveMarginCost = (row = {}) => {
  const converted = Number(row?.ConvertedNetCost ?? row?.convertedNetCost);
  if (Number.isFinite(converted) && converted > 0) return converted;
  const net = Number(row?.NetCost ?? row?.netCost ?? row?.Cost ?? row?.cost);
  return Number.isFinite(net) ? net : 0;
};

const normalizeKey = (v) => String(v ?? "").trim().toLowerCase().replace(/[\s_-]+/g, " ");
const isFixedLike = (row = {}) => {
  const costType = normalizeKey(row?.costType);
  if (costType === "fixed") return true;

  const priceType = normalizeKey(row?.priceType ?? row?.PriceType);
  if (priceType === "run charge") return false;

  const hints = [
    row?.subType,
    row?.SubType,
    row?.type,
    row?.Type,
    row?.chargeType,
    row?.priceType,
    row?.PriceType,
    row?.chargeName,
    row?.ChargeName,
    row?.name,
    row?.Name,
  ].map(normalizeKey);

  return hints.some((v) => v === "fixed" || v === "fixed charge" || v === "one time" || v === "one-time" || v === "setup fee" || v === "set up fee" || v === "set-up fee");
};
const isDecorationIncludeRow = (row = {}) => Boolean(
  row?.chargeName || row?.ChargeName || row?.decorationId || row?.DecorationId || row?.decoratorId || row?.DecoratorId
);
const customerFacingCents = (row = {}) => toCents(row?.clientPrice ?? row?.ClientPrice ?? row?.retail ?? row?.Retail ?? row?.amount);
const PROMOSTORE_INCLUDED_SELECTION_MODE = "included";

const resolveRetailMode = ({ retailMode, isPromoStore } = {}) =>
  retailMode === "independent" || isPromoStore ? "independent" : "coupled";

const normalizePromoStoreSelectionMode = (value) =>
  String(value ?? "").trim().toLowerCase().replace(/[\s_-]+/g, "_");

const flattenDecorationRowsWithMetadata = (decorations = []) =>
  (Array.isArray(decorations) ? decorations : []).flatMap((decoration) => {
    const decoratorDetails = decoration?.DecoratorDetails || decoration?.decoratorDetails || {};
    const promoStoreSelectionMode =
      decoratorDetails?.promoStoreSelectionMode ??
      decoratorDetails?.PromoStoreSelectionMode ??
      decoration?.promoStoreSelectionMode ??
      decoration?.PromoStoreSelectionMode ??
      null;
    const promoStoreSelectionGroup =
      decoratorDetails?.promoStoreSelectionGroup ??
      decoratorDetails?.PromoStoreSelectionGroup ??
      decoration?.promoStoreSelectionGroup ??
      decoration?.PromoStoreSelectionGroup ??
      null;

    return (Array.isArray(decoration?.rows) ? decoration.rows : []).map((row) => ({
      ...row,
      __isDecorationRow: true,
      __promoStoreSelectionMode: promoStoreSelectionMode,
      __promoStoreSelectionGroup: promoStoreSelectionGroup,
    }));
  });

const shouldAutoIncludePromoStoreDecorationRow = (row = {}, flags = {}) => {
  if (!flags.includeInPrice) return false;
  if (flags.costType === "fixed") return false;
  if (!row?.__isDecorationRow) return true;
  return normalizePromoStoreSelectionMode(row?.__promoStoreSelectionMode) === PROMOSTORE_INCLUDED_SELECTION_MODE;
};

const computeBaseRowPricingDetails = ({
  baseRows = [],
  includedAllocByBaseRow = [],
  retailMode = "coupled",
}) => {
  const details = [];

  for (let i = 0; i < baseRows.length; i += 1) {
    const row = baseRows[i];
    const qty = Number(row?.quantity ?? row?.Quantity ?? 0) || 0;
    const costC = toCents(getEffectiveMarginCost(row));
    const includedCostC = includedAllocByBaseRow[i] || 0;
    const rowCost = (costC * qty) + includedCostC;

    const retailC = toCents(row?.retail ?? row?.Retail ?? row?.clientPrice ?? row?.ClientPrice);
    const clientPriceC = toCents(row?.clientPrice ?? row?.ClientPrice);
    const marginPct = pickMarginPct(row?.margin, row?.Margin);
    const isPromoStoreRetailIndependent = retailMode === "independent";
    const locked = pickBool(row?.isLocked, row?.IsLocked) ||
      (!isPromoStoreRetailIndependent && pickBool(row?.isRetailLocked, row?.IsRetailLocked));
    const primarySellC = isPromoStoreRetailIndependent ? clientPriceC : retailC;
    const isStaleSell = primarySellC <= 0 || Math.abs(primarySellC - costC) <= 1;
    const hasMargin = marginPct != null && marginPct >= 0 && marginPct < 100;
    const derivedFromBaseCostC = hasMargin
      ? toCents(fromCents(costC) / (1 - (marginPct / 100)))
      : 0;
    const looksAutoDerivedFromBase = hasMargin && (
      Math.abs(primarySellC - derivedFromBaseCostC) <= 1 ||
      (
        isPromoStoreRetailIndependent &&
        clientPriceC > 0 &&
        Math.abs(clientPriceC - derivedFromBaseCostC) <= 1
      )
    );
    const shouldRepriceForInclude = includedCostC > 0 && !locked && hasMargin && (
      isStaleSell || looksAutoDerivedFromBase || isPromoStoreRetailIndependent
    );
    const canDerive =
      qty > 0 &&
      (
        shouldRepriceForInclude ||
        (!locked && hasMargin && isStaleSell)
      );
    const derivedPerUnitC = canDerive
      ? toCents((fromCents(rowCost / qty)) / (1 - (marginPct / 100)))
      : 0;
    const effectiveSellUnitC = isPromoStoreRetailIndependent
      ? (
          derivedPerUnitC ||
          clientPriceC ||
          (hasMargin && qty > 0
            ? toCents(fromCents(costC) / (1 - (marginPct / 100)))
            : 0)
        )
      : (
          canDerive
            ? derivedPerUnitC
            : retailC
        );
    const rowRevenue = effectiveSellUnitC * qty;

    details.push({
      row,
      quantity: qty,
      costC,
      includedCostC,
      rowCostCents: rowCost,
      effectiveSellUnitCents: effectiveSellUnitC,
      rowRevenueCents: rowRevenue,
      isPromoStoreRetailIndependent,
    });
  }

  return details;
};

const computeBaseRowsTotals = ({
  baseRows = [],
  includedAllocByBaseRow = [],
  retailMode = "coupled",
}) => {
  let trueCostCents = 0;
  let clientPriceCents = 0;
  let marginCents = 0;
  let marginableRevenueCents = 0;

  const details = computeBaseRowPricingDetails({
    baseRows,
    includedAllocByBaseRow,
    retailMode,
  });

  for (const detail of details) {
    trueCostCents += detail.rowCostCents;
    clientPriceCents += detail.rowRevenueCents;
    marginCents += detail.rowRevenueCents - detail.rowCostCents;
    marginableRevenueCents += detail.rowRevenueCents;
  }

  return { trueCostCents, clientPriceCents, marginCents, marginableRevenueCents };
};

export const resolveAdditionalCostFlags = (row = {}) => {
  const legacy = row?.pricingOption || row?.DisplayStatus || row?.displayStatus || "";
  const normalizedLegacy = normalizeDisplayStatus(legacy);
  const normalizedLegacyRaw = String(legacy).trim().toLowerCase();

  const subtractFromMargin =
    typeof row?.subtractFromMargin === "boolean"
      ? row.subtractFromMargin
      : normalizedLegacy === DISPLAY_STATUS.SUBTRACT_FROM_MARGIN;

  const includeInPrice =
    typeof row?.includeInPrice === "boolean"
      ? row.includeInPrice
      : normalizedLegacy === DISPLAY_STATUS.INCLUDE_IN_PRICE;

  const displayToClient =
    typeof row?.displayToClient === "boolean"
      ? row.displayToClient
      : normalizedLegacyRaw !== "hidden from client";

  const costType = isFixedLike(row) ? "fixed" : "per_unit";

  return { includeInPrice, subtractFromMargin, displayToClient, costType };
};

export const computeAdditionalCostImpact = ({
  baseRows = [],
  additionalRows = [],
  retailMode = "coupled",
}) => {
  let trueCostCents = 0;
  let clientPriceCents = 0;
  let marginCents = 0;
  let marginableRevenueCents = 0;

  const baseQuantities = baseRows.map((row) => pickNumber(row?.quantity, row?.Quantity, 0));
  const baseQty = baseQuantities.reduce((sum, qty) => sum + qty, 0);

  let totalIncludedCostCents = 0;
  let targetedDecorationIncludedCostCents = 0;
  let targetedDecorationIncludedSellCents = 0;
  additionalRows.forEach((row) => {
    const flags = resolveAdditionalCostFlags(row);
    if (!flags.includeInPrice) return;
    if (retailMode === "independent" && !shouldAutoIncludePromoStoreDecorationRow(row, flags)) return;
    const costC = toCents(getEffectiveMarginCost(row) || row?.amount);
    const qty = pickNumber(row?.quantity, row?.Quantity, baseQty);
    const multiplier = flags.costType === "fixed" ? 1 : qty;
    const lineIncludedCostC = costC * multiplier;
    totalIncludedCostCents += lineIncludedCostC;

    if (!isDecorationIncludeRow(row)) return;
    const lineSellC = customerFacingCents(row) * multiplier;
    if (lineSellC <= 0) return;
    targetedDecorationIncludedCostCents += lineIncludedCostC;
    targetedDecorationIncludedSellCents += lineSellC;
  });

  const includedAllocByBaseRow = allocateByQuantityExact(baseQuantities, totalIncludedCostCents);
  const includedAllocWithoutTargeted = allocateByQuantityExact(
    baseQuantities,
    Math.max(0, totalIncludedCostCents - targetedDecorationIncludedCostCents)
  );

  const baseWithAllIncluded = computeBaseRowsTotals({
    baseRows,
    includedAllocByBaseRow,
    retailMode,
  });
  trueCostCents += baseWithAllIncluded.trueCostCents;
  clientPriceCents += baseWithAllIncluded.clientPriceCents;
  marginCents += baseWithAllIncluded.marginCents;
  marginableRevenueCents += baseWithAllIncluded.marginableRevenueCents;

  if (retailMode !== "independent" && targetedDecorationIncludedSellCents > 0) {
    const baseWithoutTargeted = computeBaseRowsTotals({
      baseRows,
      includedAllocByBaseRow: includedAllocWithoutTargeted,
      retailMode,
    });
    const targetedEmbeddedRevenueCents = baseWithAllIncluded.clientPriceCents - baseWithoutTargeted.clientPriceCents;
    const parityDeltaCents = targetedDecorationIncludedSellCents - targetedEmbeddedRevenueCents;
    if (parityDeltaCents !== 0) {
      clientPriceCents += parityDeltaCents;
      marginCents += parityDeltaCents;
      marginableRevenueCents += parityDeltaCents;
    }
  }

  for (const row of additionalRows) {
    const flags = resolveAdditionalCostFlags(row);
    if (flags.includeInPrice) continue;
    const costC = toCents(getEffectiveMarginCost(row) || row?.amount);
    const retailC = toCents(row?.retail ?? row?.Retail ?? row?.clientPrice ?? row?.ClientPrice ?? row?.amount);
    const qty = pickNumber(row?.quantity, row?.Quantity, baseQty);
    const multiplier = flags.costType === "fixed" ? 1 : qty;

    const rowCost = costC * multiplier;
    const rowRevenue = retailC * multiplier;

    // Stage 1: true cost always includes all additional costs
    trueCostCents += rowCost;
    // Stage 2: client-facing revenue (only visible client charges)
    // - Include in Price should be bundled into SKU pricing, not added as a separate row subtotal.
    // - Subtract From Margin is internal-only and should not affect client-facing subtotal.
    if (!flags.includeInPrice && !flags.subtractFromMargin) {
      clientPriceCents += rowRevenue;
    }
    // Stage 3: margin impact
    if (flags.subtractFromMargin) {
      marginCents -= rowCost;
    } else {
      marginCents += rowRevenue - rowCost;
      marginableRevenueCents += rowRevenue;
    }
  }

  return {
    trueCostCents,
    clientPriceCents,
    marginCents,
    marginableRevenueCents,
  };
};

export const computePromoStorePricingBreakdownRows = ({
  pricingRows = [],
  decorations = [],
} = {}) => {
  const allPricingRows = Array.isArray(pricingRows) ? pricingRows : [];
  const baseRows = allPricingRows.filter((row) => String(row?.priceType || "").trim() === "Breakdown");
  const productAdditionalRows = allPricingRows.filter((row) => String(row?.priceType || "").trim() !== "Breakdown");
  const decorationAdditionalRows = flattenDecorationRowsWithMetadata(decorations);
  const baseQuantities = baseRows.map((row) => pickNumber(row?.quantity, row?.Quantity, 0));
  const totalBaseQuantity = baseQuantities.reduce((sum, qty) => sum + qty, 0);

  const productIncludedCostCents = productAdditionalRows.reduce((sum, row) => {
    const flags = resolveAdditionalCostFlags(row);
    if (!shouldAutoIncludePromoStoreDecorationRow(row, flags)) return sum;
    const costC = toCents(getEffectiveMarginCost(row) || row?.amount);
    if (costC <= 0) return sum;
    const qty = pickNumber(row?.quantity, row?.Quantity, totalBaseQuantity);
    const multiplier = flags.costType === "fixed" ? 1 : qty;
    return sum + (costC * multiplier);
  }, 0);

  const decorationIncludedCostCents = decorationAdditionalRows.reduce((sum, row) => {
    const costC = toCents(getEffectiveMarginCost(row) || row?.amount);
    if (costC <= 0) return sum;
    const flags = resolveAdditionalCostFlags(row);
    const qty = flags.costType === "fixed"
      ? 1
      : pickNumber(row?.quantity, row?.Quantity, totalBaseQuantity);
    return sum + (costC * qty);
  }, 0);

  const decorationAllocByBaseRow = allocateByQuantityExact(
    baseQuantities,
    decorationIncludedCostCents
  );
  const productAllocByBaseRow = allocateByQuantityExact(
    baseQuantities,
    productIncludedCostCents
  );
  const includedAllocByBaseRow = baseQuantities.map((_, index) =>
    (decorationAllocByBaseRow[index] || 0) + (productAllocByBaseRow[index] || 0)
  );
  return computeBaseRowPricingDetails({
    baseRows,
    includedAllocByBaseRow,
    retailMode: "independent",
  }).map((detail, index) => ({
    index,
    rowId: detail.row?.id ?? detail.row?.tempRowId ?? index,
    quantity: detail.quantity,
    clientTotal: fromCents(detail.rowRevenueCents),
    clientTotalCents: detail.rowRevenueCents,
    effectiveUnitClientPrice: detail.quantity > 0 ? fromCents(Math.round(detail.rowRevenueCents / detail.quantity)) : 0,
    effectiveCost: fromCents(detail.rowCostCents),
    includedCost: fromCents(detail.includedCostC),
  }));
};
export const computeOrderItemPricingSnapshot = ({
  pricingRows = [],
  decorations = [],
  selectedTaxRate = null,
  selectedTax = null,
  taxOptions = [],
  retailMode = "coupled",
  isPromoStore = false,
}) => {
  const allPricingRows = Array.isArray(pricingRows) ? pricingRows : [];
  const baseRows = allPricingRows.filter((row) => String(row?.priceType || "").trim() === "Breakdown");
  const productAdditionalRows = allPricingRows.filter((row) => String(row?.priceType || "").trim() !== "Breakdown");
  const decorationAdditionalRows = flattenDecorationRowsWithMetadata(decorations);
  const additionalRows = [...productAdditionalRows, ...decorationAdditionalRows];

  const {
    trueCostCents,
    clientPriceCents,
    marginCents,
    marginableRevenueCents,
  } = computeAdditionalCostImpact({
    baseRows,
    additionalRows,
    retailMode: resolveRetailMode({ retailMode, isPromoStore }),
  });

  const rateFromSnapshot = typeof selectedTaxRate === "number" ? selectedTaxRate : null;
  const rateFromTaxList = (Array.isArray(taxOptions) ? taxOptions : []).find(
    (t) => String(t?.id) === String(selectedTax)
  )?.value;
  const taxRate = Number(rateFromSnapshot ?? rateFromTaxList ?? 0) || 0;

  const subtotal = fromCents(clientPriceCents);
  const marginAmount = fromCents(marginCents);
  const trueCost = fromCents(trueCostCents);
  const taxAmount = +(subtotal * taxRate).toFixed(2);
  const total = +(subtotal + taxAmount).toFixed(2);
  const marginPercentage = clientPriceCents > 0
    ? +(((marginAmount / fromCents(clientPriceCents)) * 100).toFixed(2))
    : 0;

  return {
    subtotal,
    taxRate,
    taxAmount,
    total,
    marginAmount,
    marginPercentage,
    trueCost,
    trueCostCents,
    clientPriceCents,
    marginCents,
    marginableRevenueCents,
    calculatedAt: new Date().toISOString(),
    model: "additional-cost-v1",
  };
};
